import React, { useState, useEffect, useCallback } from 'react';
import { dashboardApi, exportApi } from '../../api/endpoints';
import { DashboardData } from '../../types';
import DateRangePicker from '../../components/common/DateRangePicker';
import { SkeletonCard } from '../../components/common/Skeleton';
import { downloadExcel } from '../../utils/downloadFile';
import {
  FiDownload,
  FiFileText,
  FiTruck,
  FiShoppingCart,
  FiBox,
  FiDatabase,
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { toast } from 'react-toastify';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#64748b'];

const ReportsPage: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardApi.get();
      setData(response.data);
    } catch {
      toast.error('Błąd pobierania danych raportów');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>Raporty</h1></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
      </div>
    );
  }

  if (!data) return <div className="page-container"><div className="alert alert-danger">Brak danych</div></div>;

  const invoiceStatusData = data.invoiceStatusBreakdown.map(s => ({
    name: s.status, count: s.count, value: s.value,
  }));

  const orderStatusData = data.orderStatusBreakdown.map(s => ({
    name: s.status, count: s.count, value: s.value,
  }));

  const formatCurrency = (n: number) => n.toLocaleString('pl-PL', { style: 'currency', currency: 'EUR' });

  const exportCards = [
    { label: 'Faktury', icon: <FiFileText />, fn: exportApi.invoicesXlsx, file: 'faktury' },
    { label: 'Zamówienia', icon: <FiShoppingCart />, fn: exportApi.purchaseOrdersXlsx, file: 'zamowienia' },
    { label: 'Wysyłki', icon: <FiTruck />, fn: exportApi.shipmentsXlsx, file: 'wysylki' },
    { label: 'Produkty', icon: <FiBox />, fn: exportApi.productsXlsx, file: 'produkty' },
    { label: 'Magazyn', icon: <FiDatabase />, fn: exportApi.inventoryXlsx, file: 'stany_mag' },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Raporty i analityka</h1>
      </div>

      {/* Date range */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-body" style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: '13px' }}>Zakres:</span>
            <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onDateFromChange={setDateFrom} onDateToChange={setDateTo} />
          </div>
        </div>
      </div>

      {/* Summary KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Zamówienia', value: data.totalPurchaseOrders, color: '#3b82f6' },
          { label: 'Wartość zamówień', value: formatCurrency(data.totalOrderValue), color: '#6366f1' },
          { label: 'Zafakturowano', value: formatCurrency(data.totalInvoicedValue), color: '#10b981' },
          { label: 'Przeterminowane', value: `${data.overdueInvoiceCount} (${formatCurrency(data.overdueInvoiceValue)})`, color: '#ef4444' },
          { label: 'Aktywne wysyłki', value: data.activeShipments, color: '#f59e0b' },
          { label: 'Produkty', value: data.totalProducts, color: '#8b5cf6' },
        ].map((kpi, i) => (
          <div key={i} className="card" style={{ borderTop: `3px solid ${kpi.color}` }}>
            <div className="card-body" style={{ padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 800, color: kpi.color }}>{kpi.value}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{kpi.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        <div className="card">
          <div className="card-header"><h3>Faktury wg miesiąca</h3></div>
          <div className="card-body" style={{ padding: '12px' }}>
            {data.invoicesByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.invoicesByMonth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366f1" name="Liczba" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Brak danych</div>}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>Status faktur</h3></div>
          <div className="card-body" style={{ padding: '12px' }}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={invoiceStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="count"
                  label={({ name, value }) => `${name}: ${value}`}>
                  {invoiceStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>Wartość faktur wg statusu</h3></div>
          <div className="card-body" style={{ padding: '12px' }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={invoiceStatusData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                <Tooltip />
                <Bar dataKey="value" fill="#10b981" name="Wartość (EUR)" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>Status zamówień</h3></div>
          <div className="card-body" style={{ padding: '12px' }}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={orderStatusData} cx="50%" cy="45%" outerRadius={80} paddingAngle={2} dataKey="count">
                  {orderStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Export section */}
      <div className="card">
        <div className="card-header"><h3>Eksport danych do Excel</h3></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
            {exportCards.map((card, i) => (
              <button
                key={i}
                className="btn btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', padding: '12px' }}
                onClick={() => downloadExcel(card.fn, `${card.file}_${new Date().toISOString().slice(0,10)}.xlsx`)}
              >
                {card.icon} <FiDownload /> {card.label} (.xlsx)
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
